import React, { useEffect, useState } from "react";
import { Copy, Check, Users, TrendingUp, Activity, Shield, Link2 } from "lucide-react";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme, THEMES } from "../context/ThemeContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

export default function InvestorDashboard() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [m, r] = await Promise.all([
          api.get("/investor/me"),
          api.get("/investor/referrals"),
        ]);
        setStats(m.data.stats);
        setReferrals(r.data.items || []);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, []);

  if (!user) return null;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const refLink = user.investor_code ? `${origin}/?ref=${user.investor_code}` : "";
  const monthPercent = stats ? Math.min(100, (stats.month_referrals / stats.cap) * 100) : 0;

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <Header />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
          <Shield className="h-3 w-3 theme-text" /> Investor console · Node affiliate
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
          Welcome, <span style={{ color: theme.color }}>{user.name || "Investor"}</span>
        </h1>
        <p className="text-zinc-400 mt-2">Grow the grid. Track your referrals, status, and monthly cap.</p>

        <div className="mt-10 grid md:grid-cols-4 gap-4" data-testid="investor-stats">
          <StatCard icon={<Users className="h-4 w-4" />} label="Total referrals" value={stats?.total_referrals ?? "…"} />
          <StatCard icon={<TrendingUp className="h-4 w-4" />} label="This month" value={stats?.month_referrals ?? "…"} sub={`of ${stats?.cap ?? 20} cap`} />
          <StatCard
            icon={<Activity className="h-4 w-4" />}
            label="Status"
            valueNode={
              <span
                className="inline-flex items-center gap-2 font-display font-black text-2xl"
                style={{ color: stats?.active ? theme.color : "#a1a1aa" }}
                data-testid="investor-active"
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: stats?.active ? theme.color : "#52525b" }} />
                {stats?.active ? "Active" : "Inactive"}
              </span>
            }
            sub={stats?.active ? "≥ 1 referral in last 30d" : "1 referral needed in 30d"}
          />
          <StatCard icon={<Users className="h-4 w-4" />} label="Credits" value={user.credits} />
        </div>

        <div className="mt-8 p-6 border border-white/10 bg-[#0A0A0A]" data-testid="investor-month-progress">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Monthly cap</div>
              <div className="font-display font-bold text-lg mt-1">
                {stats?.month_referrals ?? 0} / {stats?.cap ?? 20} referrals this month
              </div>
            </div>
            <div className="text-xs text-zinc-500 font-mono">{Math.round(monthPercent)}%</div>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full transition-all duration-700" style={{ width: `${monthPercent}%`, backgroundColor: theme.color, boxShadow: `0 0 12px ${theme.color}` }} />
          </div>
        </div>

        <div className="mt-8 p-8 border bg-[#0A0A0A]" style={{ borderColor: theme.color, boxShadow: `0 0 30px ${theme.color}33` }} data-testid="investor-referral-card">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
            <Link2 className="h-3 w-3 theme-text" /> Your referral link
          </div>
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-end">
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-3xl theme-text break-all" data-testid="investor-code">
                {user.investor_code || "—"}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 p-3 text-sm font-mono bg-black/60 border border-white/10 break-all" data-testid="investor-link">
                  {refLink}
                </code>
                <button onClick={() => copy(refLink)} className="h-11 px-4 border border-white/10 hover:border-white/30 text-sm inline-flex items-center gap-2" data-testid="investor-copy-link">
                  {copied ? <Check className="h-4 w-4 theme-text" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 flex items-center justify-between">
          <h2 className="font-display font-bold text-xl">Your referrals</h2>
          <span className="text-sm text-zinc-500">{referrals.length} total</span>
        </div>
        <div className="mt-4 border border-white/10 bg-[#0A0A0A]" data-testid="investor-referrals-table">
          {error ? (
            <p className="p-6 text-sm text-red-400">{error}</p>
          ) : referrals.length === 0 ? (
            <p className="p-6 text-sm text-zinc-400">No referrals yet. Share your link above to start building.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Name</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Email</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Tier</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Power Color</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Priority</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.map((u) => {
                  const themeMeta = THEMES[u.theme] || THEMES.jupiter_gold;
                  return (
                    <TableRow key={u.id} className="border-white/5 hover:bg-white/5" data-testid={`investor-referral-row-${u.id}`}>
                      <TableCell className="font-medium text-sm">{u.name || "—"}</TableCell>
                      <TableCell className="text-zinc-300 text-sm">{u.email}</TableCell>
                      <TableCell className="text-zinc-300 text-sm">{u.tier ? u.tier.replace("tier_", "") + " · " : "—"}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-2 text-sm text-zinc-300">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: themeMeta.color }} />
                          {themeMeta.planet}
                        </span>
                      </TableCell>
                      <TableCell className={u.has_priority_access ? "theme-text font-semibold text-sm" : "text-zinc-500 text-sm"}>
                        {u.has_priority_access ? "Premium" : "—"}
                      </TableCell>
                      <TableCell className="text-zinc-500 text-sm">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, valueNode, sub }) {
  return (
    <div className="p-5 border border-white/10 bg-[#0A0A0A]">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
        <span className="theme-text">{icon}</span> {label}
      </div>
      {valueNode ? valueNode : <div className="font-display font-black text-3xl">{value}</div>}
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}
